import React, {
  useState,
  PropsWithChildren,
  useEffect,
  useMemo,
  Fragment,
} from "react";

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import WarningIcon from "@material-ui/icons/Warning";

import { MUTATIONS, QUERIES } from "../../../../graphql";
import {
  Apartment,
  Reservation,
  ReservableServiceSlot,
  User,
  AmenityDefinition,
  AmenityReservationGuestParkingData,
} from "../../../../graphql/models";

import { ApartmentBoxPreview, GqlRecordPicker } from "../../../components";
import { dateFromUtcMinutes, ignoreDateDST } from "../../../../utils";

import useReservationFormStyle from "./reservation_form.style";
import {
  useDateTimeUtils,
  useSnackbarMessage,
  useSnackbarMutation,
} from "../../../../hooks";
import { AmenityGroupedSlot } from "../../../../graphql/models/AmenityGroupedSlot";
import { AmenitySlotsAvailabilityPreview } from "../../../components/amenity_slots_availability_preview";
import {
  BookingReservationData,
  BookingSlotDto,
} from "../../../../graphql/models/Booking";
import { DateTime } from "luxon";

export interface ReservationEditFormProps extends PropsWithChildren<any> {
  onReservationCreatedSuccessfully: (v: Reservation) => void;
}

export default function ReservationForm(props: ReservationEditFormProps) {
  const { onReservationCreatedSuccessfully } = props;
  const classes = useReservationFormStyle();

  const [invokeCreateReservation, { loading, data: createReservationData }] =
    useSnackbarMutation(MUTATIONS.CREATE_AMENITY_RESERVATION);
  const [updated, setUpdated] = useState(false);

  const { raiseWarningSnackbar } = useSnackbarMessage();
  const { durationDisplayText } = useDateTimeUtils();

  // -- Fields needed to perform the reservation --
  const [apartment, setApartment] = useState<Apartment | undefined>(void 0);
  const [targetUser, setTargetUser] = useState<User | undefined>(void 0);
  const [units, setUnits] = useState<number>(1);
  const [slots, setSlots] = useState<ReservableServiceSlot[]>([]);
  const [slotRange, setSelectedSlotRange] = useState<
    AmenityGroupedSlot | undefined
  >(void 0);
  const [esclusive, setEsclusive] = useState<boolean>(false);
  const [guestNickname, setGuestNickname] = useState("");
  const [guestPlate, setGuestPlate] = useState<string>();

  useEffect(() => {
    if (slotRange) {
      setSlots([...slotRange.slots]);
    }
  }, [slotRange]);

  // -- Fields needed to search for the slots --
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const defaultTo = useMemo(() => new Date(), []);
  defaultTo.setUTCHours(23, 59);

  const [from, setFrom] = useState<Date | null | undefined>(new Date());
  const to = useMemo(() => {
    if (from) {
      const fromCopy = new Date(from?.getTime());
      fromCopy.setUTCHours(23, 59);

      return fromCopy;
    }

    return defaultTo;
  }, [from, defaultTo]);
  const [serviceDefinition, setServiceDefinition] = useState<
    AmenityDefinition | undefined
  >(void 0);

  const isoDate: string = useMemo(() => {
    if (!from) return "";
    return DateTime.fromJSDate(from).toISODate() ?? "";
  }, [from]);

  const [bookingSlot, setBookingSlot] = useState<BookingSlotDto | undefined>(
    undefined
  );

  // Used only to search reservations.
  const [selectedDurationInMinutes, setSelectedDurationInMinutes] = useState<
    number | undefined
  >(void 0);

  useEffect(() => setBookingSlot(undefined), [selectedDurationInMinutes]);

  const triggerSetFrom = (v: Date | null | undefined) => {
    setFrom(v);
  };

  // Triggers a reset of the target user upon change of the Apartment.
  const triggerSetApartment = (value: Apartment | undefined) => {
    setTargetUser(void 0);
    setApartment(value);
  };

  useEffect(() => {
    if (apartment?.reservationConductStatus?.blocked) {
      raiseWarningSnackbar(
        `ATTENZIONE: l'appartamento ${apartment.code} è stato bloccato dal sistema in quanto non si è presentato a 3 o più prenotazioni consecutive. Anche se procedi nel compilare i campi, non potrai effettuare la prenotazione. Per sbloccare l'appartamento, visitare la sezione "Appartamenti" e sbloccare l'appartamento con l'apposito pulsante.`
      );
    }
  }, [apartment, raiseWarningSnackbar]);

  const enableUserSelectBox = useMemo(() => {
    return Boolean(apartment) && Boolean(serviceDefinition);
  }, [apartment, serviceDefinition]);

  const displayEsclusiveReservationOpts = useMemo(() => {
    return serviceDefinition?.exclusiveReservationSettings?.enabled === true;
  }, [serviceDefinition]);

  const isGuestParking = useMemo(() => {
    if (!serviceDefinition) return false;
    return serviceDefinition.isGuestParking === true;
  }, [serviceDefinition]);

  const possibleSlotRangeDuration = useMemo(() => {
    if (serviceDefinition) {
      let [minConsecutiveSlots, maxConsecutiveSlots] = [
        serviceDefinition.minConsecutiveSlots,
        serviceDefinition.maxConsecutiveSlots,
      ];
      if (esclusive) {
        minConsecutiveSlots =
          serviceDefinition.exclusiveReservationSettings?.minConsecutiveSlots ??
          0;
        maxConsecutiveSlots =
          serviceDefinition.exclusiveReservationSettings?.maxConsecutiveSlots ??
          0;
      }

      const combinations: number[] = [];
      for (let i = minConsecutiveSlots; i < maxConsecutiveSlots; i++) {
        combinations.push(i * serviceDefinition.slotDurationInMinutes);
      }

      return combinations;
    }

    return [];
  }, [serviceDefinition, esclusive]);

  const minConsecutiveSlots: number | undefined = useMemo(() => {
    if (serviceDefinition && selectedDurationInMinutes) {
      return (
        selectedDurationInMinutes / serviceDefinition.slotDurationInMinutes
      );
    }

    return void 0;
  }, [serviceDefinition, selectedDurationInMinutes]);

  const displaySlotsRangeSelectBox = useMemo(() => {
    return from && to && serviceDefinition && units && minConsecutiveSlots;
  }, [from, to, serviceDefinition, units, minConsecutiveSlots]);

  const slotRangeSearchQueryParams = useMemo(() => {
    return {
      from: from?.getTime(),
      to: to?.getTime(),
      serviceDefinitionId: serviceDefinition?.id,
      amountOfPersons: units,
      minConsecutiveSlots,
      esclusive,
    };
  }, [from, to, serviceDefinition?.id, units, minConsecutiveSlots, esclusive]);

  const { raiseErrorSnackbar } = useSnackbarMessage();

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    let guestParkingData: AmenityReservationGuestParkingData | undefined =
      undefined;
    if (isGuestParking) {
      if (!guestPlate) {
        raiseErrorSnackbar(`Inserire la targa del veicolo!`);
        return;
      }
      guestParkingData = {
        guestNickname,
        guestPlate,
        saveGuestDataInGarage: false,
      };
    }
    if (!isoDate || !bookingSlot) return;
    const bookingData: BookingReservationData = {
      isoDate,
      hour: bookingSlot.hour,
      duration: selectedDurationInMinutes,
      amenityId: serviceDefinition?.serviceId,
    };
    const variables = {
      apartmentId: apartment?.id,
      targetUserId: targetUser?.id,
      units,
      slots,
      esclusive,
      guestParkingData,
      bookingData,
    };
    invokeCreateReservation({
      variables,
    });
  };

  if (createReservationData && !updated) {
    onReservationCreatedSuccessfully(
      createReservationData.createAmenityReservation
    );
    setUpdated(true);
  }

  const saveEnabled = useMemo(() => {
    return (
      apartment &&
      serviceDefinition &&
      targetUser &&
      isoDate.length > 0 &&
      bookingSlot
    );
  }, [apartment, serviceDefinition, targetUser, isoDate, bookingSlot]);

  const userFilters = useMemo(
    () => ({
      role: "user",
      apartmentIds: [apartment?.id],
      buildingId: serviceDefinition?.building?.id,
    }),
    [apartment, serviceDefinition]
  );

  const currentlyHighlightedSlots = useMemo(() => {
    if (slotRange) {
      return slotRange.slots.map((v) => v.key);
    }

    return [];
  }, [slotRange]);

  return (
    <>
      {loading && `Loading...`}
      {!createReservationData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={6} xs={12} md={4}>
              <GqlRecordPicker
                required
                className={classes.textField}
                multiple={false}
                value={serviceDefinition}
                getOptionLabel={(record) => record.name}
                placeHolder={"Amenity"}
                renderOption={(record) =>
                  `${record.name} (${record.building.name})`
                }
                onValueChange={(value) => setServiceDefinition(value)}
                query={QUERIES.AMENITIES_DEFINITIONS}
                queryResultDataField={"amenitiesDefinitions"}
              />
            </Grid>
            <Grid item sm={6} xs={12} md={4}>
              <GqlRecordPicker
                required
                className={classes.textField}
                multiple={false}
                value={apartment}
                getOptionLabel={(record: Apartment) => record.code}
                placeHolder={"Appartamento"}
                renderOption={(record: Apartment) => (
                  <ApartmentBoxPreview apartment={record} />
                )}
                onValueChange={(value) => triggerSetApartment(value)}
                query={QUERIES.APARTMENTS}
                queryResultDataField={"apartments"}
              />
            </Grid>
            <Grid item sm={6} xs={12} md={4}>
              <GqlRecordPicker
                required
                disabled={!enableUserSelectBox}
                className={classes.textField}
                multiple={false}
                value={targetUser}
                getOptionLabel={(record: User) =>
                  `${record.lastName} ${record.firstName}`
                }
                placeHolder={"Cliente"}
                renderOption={(record) =>
                  `${record.lastName} ${record.firstName}`
                }
                onValueChange={(value) => setTargetUser(value)}
                query={QUERIES.USERS}
                queryVariables={userFilters}
                queryResultDataField={"users"}
              />
            </Grid>
            <Grid item md={3}>
              {serviceDefinition && (
                <GqlRecordPicker
                  required
                  className={classes.textField}
                  multiple={false}
                  value={selectedDurationInMinutes}
                  getOptionLabel={(record: number) =>
                    durationDisplayText(record)
                  }
                  placeHolder={"Durata"}
                  renderOption={(record: number) => durationDisplayText(record)}
                  onValueChange={(value) => setSelectedDurationInMinutes(value)}
                  query={QUERIES.AMENITY_DURATIONS}
                  queryVariables={{
                    serviceId: serviceDefinition?.serviceId,
                  }}
                  queryResultDataField={"amenityDurations"}
                />
              )}
              {!serviceDefinition && (
                <TextField
                  variant={"outlined"}
                  style={{ width: "100%" }}
                  disabled
                  label="Durata"
                />
              )}
            </Grid>
            {false && (
              <Grid item sm={6} xs={12} md={3}>
                <Select
                  variant="outlined"
                  placeholder="Durata"
                  displayEmpty
                  className={classes.textField}
                  value={selectedDurationInMinutes}
                  onBlur={(evt) =>
                    setSelectedDurationInMinutes(
                      evt.target.value === undefined
                        ? undefined
                        : Number(evt.target.value)
                    )
                  }
                >
                  <MenuItem value={undefined} disabled>
                    Seleziona la durata in minuti
                  </MenuItem>
                  {possibleSlotRangeDuration.map((v) => (
                    <MenuItem value={v}>{v} minuti</MenuItem>
                  ))}
                </Select>
              </Grid>
            )}
            <Grid item sm={6} xs={12} md={3}>
              <DateTimePicker
                ampm={false}
                // format="dd/MM/yyyy HH:mm"
                format="dd/MM/yyyy"
                className={classes.datePicker}
                label="Data desiderata"
                inputVariant="outlined"
                value={from}
                onChange={(e) => triggerSetFrom(e)}
              />
            </Grid>
            <Grid item sm={6} xs={12} md={3}>
              <TextField
                disabled={esclusive || isGuestParking}
                className={classes.textField}
                label={
                  isGuestParking ? "Numero di veicoli" : "Numero di persone"
                }
                variant="outlined"
                type="number"
                value={units}
                onChange={(e) => setUnits(Number(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {displaySlotsRangeSelectBox && (
                <GqlRecordPicker
                  required
                  className={classes.textField}
                  multiple={false}
                  value={bookingSlot}
                  getOptionLabel={(record: BookingSlotDto) => {
                    return record
                      ? `${record.displayValue} (max ${record.unitsAvailable} posti)`
                      : "-seleziona-";
                  }}
                  placeHolder={"Fascia oraria"}
                  renderOption={(record: BookingSlotDto) => {
                    return record
                      ? `${record.displayValue} (max ${record.unitsAvailable} posti)`
                      : "-seleziona-";
                  }}
                  onValueChange={(value) => {
                    setBookingSlot(value);
                  }}
                  query={QUERIES.AMENITY_FIND_AVAILABLE_SLOTS}
                  queryVariables={{
                    serviceId: serviceDefinition?.serviceId,
                    isoDate,
                    durationInMinutes: selectedDurationInMinutes,
                  }}
                  queryResultDataField={"amenityBookingFindAvailableSlots"}
                />
              )}
              {!displaySlotsRangeSelectBox && (
                <TextField
                  disabled={true}
                  variant={"outlined"}
                  style={{ width: "100%" }}
                  label="Slot da prenotare"
                ></TextField>
              )}
            </Grid>
            {displayEsclusiveReservationOpts && (
              <Grid item xs={12}>
                <Fragment>
                  <span>Oppure</span>
                  <Checkbox
                    checked={esclusive === true}
                    onChange={(evt, checked) => setEsclusive(checked)}
                  />{" "}
                  Prenotazione Esclusiva
                </Fragment>
              </Grid>
            )}
            {false && displaySlotsRangeSelectBox && (
              <Grid item sm={6} xs={6}>
                <GqlRecordPicker
                  className={classes.textField}
                  multiple={false}
                  value={slotRange}
                  keyExpr="fromTime"
                  getOptionLabel={(record: AmenityGroupedSlot) => {
                    const dateFrom = ignoreDateDST(
                      dateFromUtcMinutes(record.slots[0].date, record.fromTime)
                    );
                    const dateTo = ignoreDateDST(
                      dateFromUtcMinutes(
                        record.slots[record.slots.length - 1].date,
                        record.toTime
                      )
                    );
                    return [
                      dateFrom.toLocaleTimeString(),
                      dateTo.toLocaleTimeString(),
                    ].join("-");
                  }}
                  placeHolder={"Orari possibili"}
                  getOptionDisabled={(record: AmenityGroupedSlot) =>
                    record.available === false
                  }
                  renderOption={(record: AmenityGroupedSlot) => {
                    const dateFrom = ignoreDateDST(
                      dateFromUtcMinutes(record.slots[0].date, record.fromTime)
                    );
                    const dateTo = ignoreDateDST(
                      dateFromUtcMinutes(
                        record.slots[record.slots.length - 1].date,
                        record.toTime
                      )
                    );
                    return (
                      <>
                        {dateFrom.toLocaleDateString()}&nbsp;➜&nbsp;
                        <strong>
                          Dalle {dateFrom.toLocaleTimeString()} Alle{" "}
                          {dateTo.toLocaleTimeString()}
                        </strong>
                      </>
                    );
                  }}
                  onValueChange={(value: AmenityGroupedSlot) =>
                    setSelectedSlotRange(value)
                  }
                  query={QUERIES.AMENITY_CURRENT_SLOTS_RANGES}
                  queryResultDataField={"amenityCurrentSlotsRanges"}
                  queryVariables={slotRangeSearchQueryParams}
                />
              </Grid>
            )}
            {displaySlotsRangeSelectBox && from && to && serviceDefinition && (
              <Grid item sm={6} xs={6}>
                {serviceDefinition.alwaysEsclusive && (
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <WarningIcon />
                    <Typography variant="caption">
                      Questa amenity è configurata ad uso esclusivo. Ogni
                      prenotazione non consente la prenotazione nello stesso
                      slot per altre persone.
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
            {isGuestParking && (
              <Grid item xs={12}>
                <fieldset style={{ borderRadius: "1rem" }}>
                  <legend>Dati ospite Guest Parking</legend>
                  <Grid container spacing={1}>
                    <Grid item md={4}>
                      <TextField
                        label="Nome ospite (facoltativo)"
                        variant="outlined"
                        style={{ width: "100%" }}
                        value={guestNickname}
                        onChange={(e) => setGuestNickname(e.target.value)}
                      />
                    </Grid>
                    <Grid item md={4}>
                      <TextField
                        label="Targa veicolo (obbligatorio)"
                        required
                        variant="outlined"
                        style={{ width: "100%" }}
                        value={guestPlate}
                        onChange={(e) => setGuestPlate(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
            )}
            {/* {
              displaySlotsSelectBox && 
              <Grid item sm={12} xs={12}>
                <GqlRecordPicker
                  className={classes.textField} 
                  multiple={true}
                  value={slots}
                  keyExpr='key'
                  getOptionLabel={(record) => {
                    const date = new Date(record.date);
                    const dateFrom = dateFromUtcMinutes(date, record.fromTime);
                    const dateTo = dateFromUtcMinutes(date, record.toTime);
                    return [dateFrom.toLocaleTimeString(), dateTo.toLocaleTimeString()].join('-');
                  }}
                  placeHolder={'Slot Orari'}
                  renderOption={(record) => {
                    const date = new Date(record.date);
                    const dateFrom = dateFromUtcMinutes(date, record.fromTime);
                    const dateTo = dateFromUtcMinutes(date, record.toTime);
                    return (
                      <>
                        {date.toLocaleDateString()}&nbsp;➜&nbsp; 
                        <strong>
                          Dalle {dateFrom.toLocaleTimeString()} Alle {dateTo.toLocaleTimeString()}
                        </strong>&nbsp;({record.availableUnits}) posti rimasti
                      </>
                    )
                  }}
                  onValueChange={(value) => setSlots(value)}
                  query={QUERIES.AMENITY_CURRENT_SLOTS}
                  queryResultDataField={'amenityCurrentSlots'}
                  queryVariables={slotSearchQueryParams}
                />
              </Grid>
            } */}
            <Grid item sm={12} xs={12}>
              <Button
                disabled={!saveEnabled}
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "PRENOTA" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
